import classnames from 'classnames'
import { css } from 'emotion'
import { Product } from 'even-ts-static'
import * as React from 'react'
import { connect } from 'react-redux'

import { CommonState } from 'embed/reducers'
import { PendingResponse } from 'embed/constants'
import { ratesPendingResponsesSelector } from 'embed/selectors/rates'

import { useTheme } from 'embed/hooks'
import { PendingOriginators } from './PendingOriginators'

const styles = require('./styles')

interface OwnProps {
  loadingLabel: string
  brandColor?: string
  showPendingOriginators?: boolean
  product: Product
}

interface StoreProps {
  pendingOriginators?: PendingResponse[]
}

export type LoadingIndicatorProps = StoreProps & OwnProps

export const BRAND_COLOR = {
  TRUE: 'true',
}

const LIFE_INSURANCE_ORIGINATORS = [
  {
    imageUrl: 'https://www.leaplife.com/img/logo-prudential.svg',
    name: 'Prudential',
  },
  {
    imageUrl: 'https://www.leaplife.com/img/logo-transamerica.svg',
    name: 'Transamerica',
  },
  {
    imageUrl: 'https://www.leaplife.com/img/logo-legal-general.svg',
    name: 'Legal general',
  },
  {
    imageUrl: 'https://www.leaplife.com/img/logo-lincoln.svg',
    name: 'Lincoln',
  },
  {
    imageUrl: 'https://www.leaplife.com/img/logo-protective.svg',
    name: 'Protective',
  },
]

const formatLifeInsuranceOriginators = (originators) =>
  originators.map((originator) => ({
    partner: {
      description: 'Placeholder description',
      disclaimer: 'Placeholder disclaimer',
      imageUrl: originator.imageUrl,
      name: originator.name,
      shouldDisplayPreSelect: true,
      supportsPersonalizedOffers: true,
      supportsPreSelect: false,
      uuid: '',
    },
    productTypes: [Product.LifeInsurance],
  }))

interface LabelProps {
  loadingLabel: string
  product: Product
  showPendingOriginators?: boolean
}

const Label: React.FC<LabelProps> = ({ loadingLabel, showPendingOriginators, product }) => {
  const { fontFamilyPrimary } = useTheme()
  // Label when we want to show pending originators logos (e.g. don't say 'checking for offers from' if originators haven't loaded yet)
  const pendingOriginatorsLoadingLabel =
    product === Product.LifeInsurance ? 'Searching our network of top rated carriers' : 'Checking for offers'
  return (
    <span
      className={css`
        font-family: ${fontFamilyPrimary};
        font-size: 24px;
        font-weight: 700;
        padding: 0 20px;
      `}
    >
      {showPendingOriginators ? pendingOriginatorsLoadingLabel : loadingLabel}
    </span>
  )
}

const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  loadingLabel,
  brandColor,
  pendingOriginators,
  showPendingOriginators,
  product,
}) => {
  const { borderRadiusLarge, colorBorder } = useTheme()
  const hasLoadedOriginators = pendingOriginators !== undefined && pendingOriginators.length > 0

  const loadingIndicator =
    showPendingOriginators && pendingOriginators !== undefined && hasLoadedOriginators ? (
      <PendingOriginators product={product} pendingOriginators={pendingOriginators} />
    ) : (
      <div
        className={classnames(
          styles.spinner,
          css`
            border-left-color: ${brandColor};
          `,
        )}
      />
    )
  return (
    <div
      className={classnames(
        styles.container,
        css`
          border-color: ${colorBorder};
          border-radius: ${borderRadiusLarge};
        `,
      )}
    >
      <Label loadingLabel={loadingLabel} product={product} showPendingOriginators={showPendingOriginators} />
      {loadingIndicator}
    </div>
  )
}

const mapStateToProps = (state: CommonState, ownProps: OwnProps) => ({
  pendingOriginators:
    ownProps.product === Product.LifeInsurance
      ? formatLifeInsuranceOriginators(LIFE_INSURANCE_ORIGINATORS)
      : ratesPendingResponsesSelector(state, ownProps.product),
})

export default connect<StoreProps, {}, OwnProps>(mapStateToProps)(LoadingIndicator)
